// FLAGS: authorization
// The address boundary: converts between router paths (what `navigate`, links and the route table use, with no
// language prefix) and addresses that leave the app or come back into it: absolute links, return targets handed
// to the API or another site, targets read from a query string. `toRouterPath` checks every incoming target, so
// it is the open-redirect guard for its callers.
// The prefix is the router's basename, fixed for the page load, while route segments follow the live language.
// They agree only because a language change under a prefix is always a page load; a change in place would emit
// links that pair one language's prefix with another's path, and those land on not-found.
import { getRouterBasename } from "@src/boot/routerBasename";
import i18n, { translatePathToLocale } from "@src/i18n";
import {
  getLanguageForPath,
  hasLanguagePathPrefix,
  stripLanguagePathPrefix,
} from "@src/utils/languagePathPrefix";
import getSafeRedirectPath from "@src/utils/redirect";

// A router path under the current language prefix, if any. For a target the app hands out and gets back later,
// so the path says which language it is written in. One already prefixed, or not starting with `/`, is left alone.
export const toPrefixedPath = (path: string): string =>
  !path.startsWith("/") || hasLanguagePathPrefix(path)
    ? path
    : `${getRouterBasename() ?? ""}${path}`;

// An absolute link to a router path under the current language prefix: share links, email deep links.
export const toExternalHref = (path: string): string =>
  `${globalThis.location.origin}${toPrefixedPath(path)}`;

// A target from outside, as a router path in the current language: `null` unless it is an app-relative path, and
// the caller picks the fallback.
// A language prefix is stripped and the route translated from the language it names; a bare target is taken to
// be in the current language already. Done with the flag off too, so a link made while it was on still lands.
export const toRouterPath = (incoming: unknown): string | null => {
  const safe = getSafeRedirectPath(incoming);
  if (safe === null) {
    return null;
  }
  const cut = safe.search(/[?#]/);
  const pathname = cut === -1 ? safe : safe.slice(0, cut);
  const rest = cut === -1 ? "" : safe.slice(cut);
  const language = getLanguageForPath(pathname);
  if (language === undefined) {
    return safe;
  }
  const translated = translatePathToLocale(
    stripLanguagePathPrefix(pathname),
    language,
    i18n.language ?? language,
  );
  // Checked again: stripping `/de` from `/de//evil.example` leaves a protocol-relative URL.
  return getSafeRedirectPath(`${translated}${rest}`);
};
